import React from "react";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Text from "connectui/component/display/Text";
import TextInput from "../TextInput";
import ChipText from "connectui/component/display/chip/ChipText";
import ChipRow from "connectui/component/display/chip/ChipRow";
import XChipBase from "connectui/component/display/chip/base/XChipBase";
import FilterListChip from "ui/component/inbox/filterlist/FilterListChip";
import {validateEmail} from "utilities/helperFunctions";

const useStyles = (theme) =>
  buildStyles({
    chip: {
      backgroundColor: theme.colors.hoverGray,
      color: theme.colors.gray700,
      width: "fit-content",
      borderRadius: theme.spacing(4),
      padding: theme.spacing(0.5, 2.5),
      whiteSpace: "nowrap",
      display: "flex",
      cursor: "pointer",
      overflow: "hidden",
      "&:hover": {backgroundColor: theme.colors.hoverGray},
    },
  });

function getChips({
  emailAddresses,
  deleteAddress,
  classes,
  preserveFirstEmail,
}) {
  if (!emailAddresses?.length) return [];

  const chips = [];
  const chipStyles = {
    typographyVariant: "textSM",
    weight: "medium",
    color: "gray700",
    className: classes.chip,
  };
  const xChipProps = !preserveFirstEmail
    ? {
        chipBase: XChipBase,
        onXClick: () => deleteAddress(emailAddresses[0]),
      }
    : {};

  chips.push({
    Component: ChipText,
    text: emailAddresses[0],
    ...chipStyles,
    textProps: {
      title: emailAddresses[0],
      sx: {textOverflow: "ellipsis", overflow: "hidden"},
    },
    ...xChipProps,
  });
  if (emailAddresses.length > 1) {
    chips.push({
      Component: FilterListChip,
      value: `+${emailAddresses.length - 1}`,
      ...chipStyles,
      textColor: "gray700",
      tooltip: {
        tooltipContent: (
          <ChipRow
            wrap
            sx={{
              maxWidth: 350,
              maxHeight: 200,
              overflow: "auto",
            }}
            chips={emailAddresses.slice(1).map((address) => ({
              Component: ChipText,
              chipBase: XChipBase,
              text: address,
              ...chipStyles,
              textProps: {
                title: address,
                sx: {textOverflow: "ellipsis", overflow: "hidden"},
              },
              onXClick: () => deleteAddress(address),
            }))}
          />
        ),
      },
      sx: {minWidth: "max-content"},
    });
  }

  return chips;
}

const RecipientsRow = ({
  type,
  label,
  email,
  setEmail,
  preserveFirstEmail = false,
}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const recipientInput = `${type}Input`;

  const handleDeleteAddress = (address) => {
    setEmail({
      [type]: email[type].filter((emailAddress) => emailAddress !== address),
    });
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "Tab" || e.key === " ") {
      e.preventDefault();
      if (validateEmail(email[recipientInput])) {
        setEmail({
          [recipientInput]: "",
          [type]: email[type].concat(email[recipientInput]),
        });
      }
    }
  };

  const onBlur = () => {
    if (validateEmail(email[recipientInput])) {
      setEmail({
        [recipientInput]: "",
        [type]: email[type].concat(email[recipientInput]),
      });
    }
  };

  return (
    <FlexContainer
      sx={{width: "100%", height: "28px"}}
      alignItems="center"
      gap={theme.spacing(1)}
    >
      <Text
        variant="textSM"
        weight="medium"
        color="gray600"
        sx={{marginRight: theme.spacing(1.5)}}
      >
        {label}
      </Text>
      <ChipRow
        chips={getChips({
          emailAddresses: email[type],
          deleteAddress: handleDeleteAddress,
          classes,
          preserveFirstEmail,
        })}
        sx={{width: "fit-content", overflow: "hidden"}}
      />
      <TextInput
        inputProps={{
          value: email[recipientInput],
          onKeyDown,
          onBlur,
          onChange: (e) => {
            const val = e.target.value;
            setEmail({[recipientInput]: val});
          },
          style: {...theme.typography.textSM.medium},
        }}
        sx={{flex: 1, minWidth: "170px"}}
      />
    </FlexContainer>
  );
};

const EmailRecipients = ({email, setEmail}) => {
  const {theme} = useTheme();

  return (
    <FlexContainer
      sx={{width: "100%", padding: theme.spacing(0, 4, 2)}}
      flexDirection="column"
      gap={theme.spacing(2)}
    >
      <RecipientsRow
        type={"to"}
        label={"To:"}
        email={email}
        setEmail={setEmail}
        preserveFirstEmail={true}
      />
      <RecipientsRow
        type={"cc"}
        label={"CC:"}
        email={email}
        setEmail={setEmail}
      />
      <RecipientsRow
        type={"bcc"}
        label={"BCC:"}
        email={email}
        setEmail={setEmail}
      />

      {/* SUBJECT */}
      <FlexContainer
        sx={{width: "100%", height: "28px"}}
        alignItems="center"
        gap={theme.spacing(2.5)}
      >
        <Text variant="textSM" weight="medium" color="gray600">
          {"Subject:"}
        </Text>
        <TextInput
          inputProps={{
            value: email.subject,
            onChange: (e) => {
              const val = e.target.value;
              setEmail({subject: val});
            },
            style: {...theme.typography.textSM.medium},
          }}
        />
      </FlexContainer>
    </FlexContainer>
  );
};

export default EmailRecipients;
